import {
  CampaignImpressionsByLocationRO,
  CampaignTopicsByLocationRO,
  GetProgrammaticCampaignAccountAndPersonaInsightsRO,
  GetProgrammaticCampaignAccountsInBuyerResearchStageRO,
  GetProgrammaticCampaignAccountsLiftRO,
  GetProgrammaticCampaignActiveAccountEngagementsRO,
  GetProgrammaticCampaignBuyerResearchStageEngagementRO,
  GetProgrammaticCampaignCreativeEngagementTestingRO,
  GetProgrammaticCampaignCreativesEngagementsRO,
  GetProgrammaticCampaignEngagementAcrossPersonasRO,
  GetProgrammaticCampaignEngagementBreakdownRO,
  GetProgrammaticCampaignFatigueMapRO,
  GetProgrammaticCampaignIntegratedCampaignReportRO,
  GetProgrammaticCampaignMobileDeliveryBreakdownRO,
  GetProgrammaticCampaignOverallVideoViewsRO,
  GetProgrammaticCampaignPerformanceByDeviceRO,
  GetProgrammaticCampaignStatisticsRO,
  GetProgrammaticCampaignTopAccountsRO,
  GetProgrammaticCampaignTopCreativePerformanceRO,
  GetProgrammaticCampaignVideoCompletionRateRO,
  GetProgrammaticOverallViewabilityRO,
  SiteEngagementLiftRO,
} from "@intentsify/dto";
import { apiService, Endpoints } from "api";
import {
  DisplayPerformanceFilters,
  DisplayPerformanceRequests,
} from "../DisplayPerformanceShared";

const withFiltersParams = (filters: DisplayPerformanceFilters) => ({
  params: {
    yearFrom: filters.yearFrom,
    yearTo: filters.yearTo,
    weekFrom: filters.weekFrom,
    weekTo: filters.weekTo,
  },
});

// Campaign Engagement requests

const getStatistics = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignStatisticsRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.Statistics(id),
    withFiltersParams(filters)
  );
  return data;
};

const getImpressionsByLocation = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<CampaignImpressionsByLocationRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.ImpressionsByLocation(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getTopicsByLocation = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<CampaignTopicsByLocationRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.TopicsByLocation(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getIntegratedCampaignReport = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignIntegratedCampaignReportRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.IntegratedCampaignReport(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const topCreativePerformance = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignTopCreativePerformanceRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.TopCreativePerformance(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getEngagementBreakdown = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignEngagementBreakdownRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.EngagementBreakdown(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getOverallViewability = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticOverallViewabilityRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.OverallViewability(id),
    withFiltersParams(filters)
  );
  return data;
};

const getPerformanceByDevice = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignPerformanceByDeviceRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.PerformanceByDevice(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getMobileDeliveryBreakdown = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignMobileDeliveryBreakdownRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.MobileDeliveryBreakdown(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getSiteEngagementLift = async (
  id: string
): Promise<SiteEngagementLiftRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.ImpressionsAndClicks.Get.SiteEngagementLift(
      id
    )
  );
  return data;
};

// Accounts Performance requests

const getAccountLifts = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignAccountsLiftRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.AccountLifts(id),
    withFiltersParams(filters)
  );
  return data;
};

const getAccountInBuyerResearchStage = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignAccountsInBuyerResearchStageRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.AccountsInBuyerResearchStage(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

const getTopAccounts = async (
  id: string | number,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignTopAccountsRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.TopAccounts(id),
    withFiltersParams(filters)
  );
  return data;
};

const getAccountEngagement = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignActiveAccountEngagementsRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.AccountEngagement(id),
    withFiltersParams(filters)
  );
  return data;
};

const getAccountAndPersonasInsights = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignAccountAndPersonaInsightsRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.AccountAndPersonasInsights(id),
    withFiltersParams(filters)
  );
  return data;
};

const getEngagementAcrossPersonas = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignEngagementAcrossPersonasRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.Accounts.Get.EngagementAcrossPersonas(id),
    withFiltersParams(filters)
  );
  return data;
};

// Creative Engagement requests

const getCreativeWithHighestEngagement = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignCreativesEngagementsRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignCreativesEngagementsRO>(
      Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.CreativeWithHighestEngagement(
        id
      ),
      withFiltersParams(filters)
    );
  return data;
};

const getBuyerResearchStageEngagement = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignBuyerResearchStageEngagementRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignBuyerResearchStageEngagementRO>(
      Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.BuyerResearchStageEngagement(
        id
      ),
      withFiltersParams(filters)
    );
  return data;
};

const getCreativeFatigueMap = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignFatigueMapRO[]> => {
  const { data } = await apiService.get<GetProgrammaticCampaignFatigueMapRO[]>(
    Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.FatigueMap(id),
    withFiltersParams(filters)
  );
  return data;
};

const getOverallVideoViews = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignOverallVideoViewsRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignOverallVideoViewsRO>(
      Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.OverallVideoMap(
        id
      ),
      withFiltersParams(filters)
    );
  return data;
};

const getVideoCompletionRate = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignVideoCompletionRateRO> => {
  const { data } =
    await apiService.get<GetProgrammaticCampaignVideoCompletionRateRO>(
      Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.VideoCompletionRate(
        id
      ),
      withFiltersParams(filters)
    );
  return data;
};

const getEngagementTestingResults = async (
  id: string,
  filters: DisplayPerformanceFilters
): Promise<GetProgrammaticCampaignCreativeEngagementTestingRO> => {
  const { data } = await apiService.get(
    Endpoints.ProgrammaticCampaigns.CreativeEngagement.Get.EngagementTestingResults(
      id
    ),
    withFiltersParams(filters)
  );
  return data;
};

export const programmaticRequests: DisplayPerformanceRequests = {
  campaignEngagement: {
    getStatistics,
    getImpressionsByLocation,
    getTopicsByLocation,
    topCreativePerformance,
    getEngagementBreakdown,
    getOverallViewability,
    getPerformanceByDevice,
    getMobileDeliveryBreakdown,
    getSiteEngagementLift,
  },
  accountsPerformance: {
    getStatistics,
    getIntegratedCampaignReport,
    getAccountLifts,
    getAccountInBuyerResearchStage,
    getTopAccounts,
    getAccountEngagement,
    getAccountAndPersonasInsights,
    getEngagementAcrossPersonas,
  },
  creativeEngagement: {
    getCreativeWithHighestEngagement,
    getBuyerResearchStageEngagement,
    getCreativeFatigueMap,
    getOverallVideoViews,
    getVideoCompletionRate,
    getEngagementTestingResults,
  },
};
